import React, { ReactElement, useContext, useMemo } from 'react';
import WorkspaceContext, { WorkspaceContextType } from '../../components/Context/WorkspaceContext';
import { DynamicReportModal, useDynamicReportOptions } from '../../actions/useDynamicReportAction';
import ViewContext from '../Context/ViewContext';
import NominalJournalDynamicReportQuery from '../../graphql/queries/nominal/NominalJournalDynamicReportQuery.graphql';
import DynamicReportContext from '../Context/DynamicReportContext';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import FluentModal from '../modals/FluentModal';

export interface UseNominalJournalDynamicReportParams {
  dynamicReportModalOpen: boolean;
  setDynamicReportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  actionCriteria: any;
}

const useNominalJournalReport = ({
  dynamicReportModalOpen,
  setDynamicReportModalOpen,
  actionCriteria,
}: UseNominalJournalDynamicReportParams): ReactElement => {
  const workspaceContextValue: WorkspaceContextType = useMemo(
    () =>
      ({
        workspaceFilters: actionCriteria.filters,
        workspaceSelection: { selectedAll: actionCriteria.selectedAll },
      } as WorkspaceContextType),
    [actionCriteria.filters, actionCriteria.selectedAll]
  );

  const { viewScreenName } = useContext(ViewContext);
  const connectedForm = useContext(ConnectedFormContext);

  const dynamicReportView: any = {
    orderBy: [
      {
        fieldName: 'nominalJournal.documentDate',
        isDescending: true,
      },
    ],
    columns: [
      {
        name: 'Post Date',
        fieldName: 'nominalJournal.documentDate',
        title: 'Post Date',
      },
      {
        name: 'Reverse Date',
        fieldName: 'nominalJournal.reverseDate',
        title: 'Reverse Date',
      },
      {
        name: 'Reversing Journal Id',
        fieldName: 'nominalJournal.reversedFromId',
        title: 'Reversing Journal',
      },
      {
        name: 'Document Number',
        fieldName: 'nominalJournal.documentNumber',
        title: 'Document Number',
      },
      {
        name: 'Document Ref',
        fieldName: 'nominalJournal.documentReference',
        title: 'Document Ref',
      },
      {
        name: 'Nominal',
        fieldName: 'nominal',
        title: 'Nominal',
      },
      {
        name: 'Branch / Warehouse / Depot',
        fieldName: 'dimensionValueEmbedded.dimensionReference1.description',
        title: 'Branch / Warehouse / Depot',
      },
      {
        name: 'Type',
        fieldName: 'nominal.accountType',
        title: 'Type',
      },
      {
        name: 'Comment',
        fieldName: 'documentReference',
        title: 'Comment',
      },
      {
        name: 'VAT Type',
        fieldName: 'journalVatType',
        title: 'VAT Type',
      },
      {
        name: 'VAT Cat',
        fieldName: 'vatProductCategory',
        title: 'VAT Cat',
      },
      {
        name: 'Debit Amount',
        fieldName: 'debitAmount',
        title: 'Debit Amount',
      },
      {
        name: 'Credit Amount',
        fieldName: 'creditAmount',
        title: 'Credit Amount',
      },
    ],
    name: 'default',
    screen: viewScreenName,
  };

  const dynamicReportContextValue = useMemo(
    () => ({
      reportQuery: NominalJournalDynamicReportQuery,
      getQueryInput: (defaultQueryInput: any) => ({
        ...defaultQueryInput,
        canonicalEntityName: 'NominalJournal',
        actionCriteria: actionCriteria,
      }),
    }),
    [actionCriteria]
  );

  const { options } = useDynamicReportOptions();

  const handleClose = () => {
    setDynamicReportModalOpen(false);
    if (connectedForm?.afterSubmit) {
      connectedForm.afterSubmit();
    }
  };

  return (
    <FluentModal isOpen={dynamicReportModalOpen} styles={{ main: { minWidth: '500px', paddingBottom: '60px' } }}>
      <WorkspaceContext.Provider value={workspaceContextValue}>
        <DynamicReportContext.Provider value={dynamicReportContextValue}>
          <DynamicReportModal
            title={`Nominal Journal Detail`}
            entityType={'NominalJournalLine'}
            onClose={handleClose}
            view={dynamicReportView}
            options={options}
          />
        </DynamicReportContext.Provider>
      </WorkspaceContext.Provider>
    </FluentModal>
  );
};

export default useNominalJournalReport;
